import React, { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCheckCircle, faPlay, faStar } from '@fortawesome/pro-regular-svg-icons';
import { faStar as faStarSolid } from '@fortawesome/pro-solid-svg-icons';
import Loader from '../General/Loader';
import ShopmyImage from '../General/ShopmyImage';
import { getProductMentionDetail, getProductMentionRunDetail, searchCatalogForRetag, setProductMentionReview, setProductMentionOverride, rejectProductMentionRuns, setProductMentionRunTag, retryProductMentionRuns } from '../../api/product_mentions';
import { getPrettyNumber } from '../../helpers/formatting';
import { getCreatorProfileUrl, getShopProductUrl } from '../../helpers/helpers';
import {
  METHOD_META,
  OVERRIDE_REASONS,
  bandOf,
  getPercentDisplay,
  getPlatformDisplay,
  getMissEvidence,
  getVisualEvidence,
  getOriginalPostLabel,
  getYoutubeEmbedUrl,
  isUnmatchedRow
} from '../../helpers/product_mention_helpers';
import ProductMentionScorePanel from './ProductMentionScorePanel';
import './ProductMentionDrawer.scss';

const SEARCH_DEBOUNCE_MS = 350;

const MODE_COPY = {
  override: {
    eyebrow: 'Override tag',
    retagLabel: 'Re-tag as',
    hint: 'Overrides feed the retraining set. Reasons are required.',
    saveLabel: 'Save override',
    savePrompt: 'Pick a product to override',
    rejectLabel: 'Not a product'
  },
  tag: {
    eyebrow: 'Tag a product',
    retagLabel: 'Tag as',
    hint: 'Tagging a missed product feeds the retraining set.',
    saveLabel: 'Save tag',
    savePrompt: 'Pick a product to tag',
    rejectLabel: 'Correct — no product'
  },
  runView: { eyebrow: 'Run details' }
};

const ProductMentionDrawer = props => {
  const { id, kind, onClose, onChanged, onExemplar, onQualityScore, scoreResult, isScoring, onScoreMention } = props;
  const run = kind === 'run';

  const [detail, setDetail] = useState(null);
  const [query, setQuery] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [pick, setPick] = useState(null);
  const [reason, setReason] = useState(OVERRIDE_REASONS[0].value);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingQualityScore, setIsSavingQualityScore] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);

  // Row detail (includes the run record for caption/signal/model)
  useEffect(() => {
    setVideoFailed(false);
    const load = run ? getProductMentionRunDetail(id) : getProductMentionDetail(id);
    let cancelled = false;
    load.then(data => !cancelled && setDetail(data)).catch(err => !cancelled && window.ALERT.error(err));
    return () => { cancelled = true; };
  }, [id, run, scoreResult]);

  // Debounced catalog search, scored by the detector's own matcher
  const debounceRef = useRef(null);
  const searchIdRef = useRef(0);
  const changeQuery = value => {
    setQuery(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const searchId = ++searchIdRef.current;
      searchCatalogForRetag(value)
        .then(data => searchId === searchIdRef.current && setCandidates(data.candidates))
        .catch(err => window.ALERT.error(err));
    }, SEARCH_DEBOUNCE_MS);
  };
  useEffect(() => () => clearTimeout(debounceRef.current), []);

  useEffect(() => {
    const onKey = e => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const row = detail?.productMention;
  const record = detail?.runRecord;
  const postVideo = !videoFailed && (row?.postVideo || record?.video);
  const youtubeEmbed = !postVideo && getYoutubeEmbedUrl(row?.postUrl);
  const unmatched = row ? isUnmatchedRow(row) : run;
  // Mention rows take the override flow; run rows take the tag flow while
  // unreviewed with no matches, and are read-only otherwise.
  const mode = run ? (unmatched ? 'tag' : 'runView') : 'override';
  const copy = MODE_COPY[mode];
  const noMatch = run && !Number(row?.matchCount);
  const method = row ? METHOD_META[row.method] || METHOD_META.text : null;
  const matchEntry = record?.identified?.find(entry => entry.product_id === row?.Product_id);
  const signal = matchEntry?.evidence || matchEntry?.reference?.quote || '—';
  const modelRow = {
    key: 'Model',
    value: [row?.pipelineVersion, row?.runCompletedAt && moment(row.runCompletedAt).fromNow()].filter(Boolean).join(' · '),
    className: 'pmd-model'
  };
  const detectorRows = noMatch
    ? [...getMissEvidence(record), ...getVisualEvidence(record), modelRow]
    : [
        { key: 'Method', value: method?.display },
        { key: 'Signal', value: signal, className: 'pmd-signal' },
        ...getVisualEvidence(record, row?.Product_id),
        modelRow
      ];
  const detectorProduct = row && (
    <>
      {row.productImage ? <ShopmyImage className='pmd-thumb' source={row.productImage} alt='' /> : <div className='pmd-thumb placeholder' />}
      <div className='pmd-detector-text'>
        <div className='pmd-product-title'>
          {noMatch
            ? 'No product found'
            : `${row.productTitle}${row.matchCount > 1 ? ` · +${row.matchCount - 1} more` : ''}`}
        </div>
        <div className='pmd-sub'>{noMatch ? '—' : `${row.brandName || '—'} · #${row.Product_id}`}</div>
      </div>
    </>
  );

  const submit = action =>
    action()
      .then(onChanged)
      .catch(err => window.ALERT.error(err))
      .finally(() => setIsSaving(false));
  const saveOverride = () => {
    if (!pick) return;
    setIsSaving(true);
    submit(() =>
      mode === 'tag' ? setProductMentionRunTag(id, pick.Product_id) : setProductMentionOverride(id, { Product_id: pick.Product_id, reason })
    );
  };
  const keepMatch = () => {
    setIsSaving(true);
    submit(() => setProductMentionReview([id], 'confirmed'));
  };
  const rejectRow = () => {
    setIsSaving(true);
    submit(() => (mode === 'tag' ? rejectProductMentionRuns([id]) : setProductMentionReview([id], 'rejected')));
  };
  const retryRun = () => {
    setIsSaving(true);
    submit(() => retryProductMentionRuns([id]));
  };
  // The parent owns the API call and list sync; the drawer only mirrors the flip locally.
  const toggleExemplar = () => {
    setDetail(prev => ({ ...prev, productMention: { ...prev.productMention, isExemplar: !row.isExemplar } }));
    onExemplar(row);
  };
  const setQualityScore = async adminQualityScore => {
    setIsSavingQualityScore(true);
    try {
      const saved = await onQualityScore(row, adminQualityScore);
      setDetail(prev => ({ ...prev, productMention: { ...prev.productMention, adminQualityScore: saved.adminQualityScore } }));
    } catch (err) {
      window.ALERT.error(err);
    } finally {
      setIsSavingQualityScore(false);
    }
  };

  return (
    <div className='product-mention-drawer'>
      <div className='pmd-scrim' onClick={onClose} />
      <div className='pmd-panel'>
        {!row ? (
          <div className='pmd-loading'>
            <Loader size={48} />
          </div>
        ) : (
          <>
            <div className='pmd-header'>
              <div>
                <div className='pmd-eyebrow'>{copy.eyebrow}</div>
                {row.creatorUsername ? (
                  <a className='pmd-title' href={getCreatorProfileUrl(row.creatorUsername)} target='_blank' rel='noopener noreferrer'>
                    @{row.creatorUsername}
                  </a>
                ) : (
                  <div className='pmd-title'>Post #{row.SocialPost_id}</div>
                )}
                <div className='pmd-sub'>
                  {getPlatformDisplay(row.platform)} · {moment(row.contentPublishedAt || row.createdAt).fromNow(true)}
                  {row.postViews != null && ` · ${getPrettyNumber(row.postViews)} views`}
                </div>
              </div>
              <div className='pmd-header-actions'>
                {!run && row.reviewStatus !== 'rejected' && (
                  <button type='button' className={cn('pmd-strong-toggle', { active: Boolean(row.isExemplar) })} onClick={toggleExemplar}>
                    <FontAwesomeIcon icon={row.isExemplar ? faStarSolid : faStar} />
                    {row.isExemplar ? 'Strong example' : 'Mark as strong'}
                  </button>
                )}
                <button type='button' className='pmd-close' onClick={onClose} aria-label='Close'>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            </div>

            <div className='pmd-post'>
              <div className='pmd-post-player'>
                {postVideo ? (
                  <video
                    key={id}
                    className='pmd-post-media'
                    src={postVideo}
                    poster={row.postImage || undefined}
                    controls
                    playsInline
                    preload='metadata'
                    onError={() => setVideoFailed(true)}
                  />
                ) : youtubeEmbed ? (
                  <iframe
                    className='pmd-post-media pmd-post-embed'
                    src={youtubeEmbed}
                    title='YouTube video'
                    allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                    allowFullScreen
                  />
                ) : row.platform === 'youtube' && row.postUrl ? (
                  <a className='pmd-post-still-link' href={row.postUrl} target='_blank' rel='noopener noreferrer'>
                    {row.postImage ? <ShopmyImage className='pmd-post-media' source={row.postImage} alt='' /> : <div className='pmd-post-media placeholder' />}
                    <span className='pmd-post-play' aria-hidden>
                      <FontAwesomeIcon icon={faPlay} />
                    </span>
                  </a>
                ) : row.postImage ? (
                  <ShopmyImage className='pmd-post-media' source={row.postImage} alt='' />
                ) : (
                  <div className='pmd-post-media placeholder' />
                )}
              </div>
              <div className='pmd-post-text'>
                <p className='pmd-caption'>{record?.caption || 'No caption text on this post.'}</p>
                {row.postUrl && (
                  <a href={row.postUrl} target='_blank' rel='noopener noreferrer'>
                    {getOriginalPostLabel(row.platform)}
                  </a>
                )}
              </div>
            </div>

            <div className='pmd-detector'>
              <div className='pmd-eyebrow'>Detector output</div>
              <div className='pmd-detector-product'>
                {noMatch ? (
                  detectorProduct
                ) : (
                  <a className='pmd-detector-product-link' href={getShopProductUrl(row.Product_id)} target='_blank' rel='noopener noreferrer'>
                    {detectorProduct}
                  </a>
                )}
                <div className='pmd-confidence'>
                  <div className={cn('pmd-confidence-value', bandOf(row.confidence))}>{getPercentDisplay(row.confidence)}</div>
                  <div className='pmd-eyebrow'>Confidence</div>
                </div>
              </div>
              <div className='pmd-detector-rows'>
                {detectorRows.map(({ key, value, className }) => (
                  <div key={key} className='pmd-detector-row'>
                    <span className='pmd-detector-key'>{key}</span>
                    <span className={className}>{value}</span>
                  </div>
                ))}
              </div>
            </div>

            {!run && row.reviewStatus !== 'rejected' && (
              <div className='pmd-score'>
                <div className='pmd-eyebrow'>Score</div>
                <ProductMentionScorePanel
                  qualityScore={row.qualityScore}
                  adminQualityScore={row.adminQualityScore}
                  isScoring={isScoring}
                  isSavingQualityScore={isSavingQualityScore || isSaving}
                  onScore={onScoreMention}
                  onHumanScore={setQualityScore}
                />
              </div>
            )}

            {mode !== 'runView' && (
              <>
                <div className='pmd-retag'>
                  <label className='pmd-label'>{copy.retagLabel}</label>
                  <input value={query} onChange={e => changeQuery(e.target.value)} placeholder='Search catalog by product or brand' className='pmd-search' />
                  {candidates.length > 0 && (
                    <div className='pmd-candidates'>
                      {candidates.map(candidate => (
                        <div
                          key={candidate.Product_id}
                          className={cn('pmd-candidate', { picked: pick?.Product_id === candidate.Product_id })}
                          onClick={() => setPick(candidate)}
                        >
                          {candidate.image ? <ShopmyImage className='pmd-thumb' source={candidate.image} alt='' /> : <div className='pmd-thumb placeholder' />}
                          <div className='pmd-candidate-text'>
                            <div className='pmd-candidate-title'>{candidate.title}</div>
                            <div className='pmd-sub'>
                              {candidate.brand || '—'} · #{candidate.Product_id}
                            </div>
                          </div>
                          <span className='pmd-candidate-score'>{Number(candidate.score).toFixed(2)}</span>
                          <FontAwesomeIcon icon={faCheckCircle} className='pmd-candidate-check' />
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className='pmd-reason'>
                  {mode === 'override' && (
                    <>
                      <label className='pmd-label'>Reason</label>
                      <select value={reason} onChange={e => setReason(e.target.value)}>
                        {OVERRIDE_REASONS.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.display}
                          </option>
                        ))}
                      </select>
                    </>
                  )}
                  <p className='pmd-hint'>{copy.hint}</p>
                </div>

                <div className='pmd-actions'>
                  <button type='button' className='save' disabled={!pick || isSaving} onClick={saveOverride}>
                    {pick ? copy.saveLabel : copy.savePrompt}
                  </button>
                  <div className='pmd-actions-row'>
                    {mode === 'tag' ? (
                      <button type='button' className='retry' disabled={isSaving} onClick={retryRun}>
                        Try again
                      </button>
                    ) : (
                      <button type='button' className='keep' disabled={isSaving} onClick={keepMatch}>
                        Keep match
                      </button>
                    )}
                    <button type='button' className='reject' disabled={isSaving} onClick={rejectRow}>
                      {copy.rejectLabel}
                    </button>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ProductMentionDrawer;
